import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, catchError, filter, from, mergeMap, tap, throwError } from 'rxjs';

const isHttpResponse = (e: HttpEvent<unknown>): e is HttpResponse<unknown> => e instanceof HttpResponse;

export const responseInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const toastrService = inject(ToastrService);

    // Clone the request object
    const newReq = req.clone();

    const showSuccess = newReq.method !== 'GET'; // oder ['POST','PUT','PATCH','DELETE'].includes(newReq.method)

    return next(newReq).pipe(
        catchError((error) =>
            parseBlobError(error).pipe(
                catchError((err) => {
                    checkErrors(err, toastrService);
                    return throwError(() => err);
                })
            )
        ),
        filter(isHttpResponse),
        tap((resp: HttpResponse<any>) => {
            if (showSuccess && (resp.status === 200 || resp.status === 201 || resp.status === 204) && !resp?.body?.accessToken) {
                toastrService.success('Operation Successful', 'Success');
            }
        })
    );
};

const parseBlobError = (error: HttpErrorResponse) => {
    if (error.error instanceof Blob) {
        return from(error.error.text()).pipe(
            mergeMap((text) => {
                try {
                    const json = JSON.parse(text);
                    return throwError(() => ({
                        ...error,
                        error: json, // <-- jetzt greift dein bestehender Code
                    }));
                } catch {
                    return throwError(() => ({
                        ...error,
                        error: { detail: text },
                    }));
                }
            })
        );
    }

    return throwError(() => error);
};

const checkErrors = (error: any, toastrService: ToastrService) => {
    console.error('HTTP error occurred:', error);

    let validationErrorsHandled = false;
    if (error.error?.errors) {
        validationErrorsHandled = handleValidationErrors(error.error.errors, toastrService);
    }

    if (error.error?.detail) {
        toastrService.error(error.error.detail, 'Error', {
            timeOut: 30000,
            progressBar: false,
        });
    } else if (error.error?.title) {
        if (!validationErrorsHandled) {
            toastrService.error(error.error.title, 'Error', {
                timeOut: 30000,
                progressBar: false,
            });
        }
    } else {
        let message = 'An unknown error occured';
        message += ': Status: ' + error.status;
        message += ', ' + error.statusText;

        if (error.error && typeof error.error === 'string') {
            message += ', ' + error.error;
        }

        toastrService.error(message, 'Error', {
            timeOut: 30000,
            progressBar: false,
        });
    }
};

const handleValidationErrors = (errors: { [key: string]: string[] }, toastrService: ToastrService): boolean => {
    let validationErrorsHandled = false;
    Object.keys(errors).forEach((key) => {
        // Lowercase the first character of the key
        const formControlKey = key && key.charAt(0).toLowerCase() + key.slice(1);

        validationErrorsHandled = true;

        // Display errors via ToastrService
        errors[key].forEach((errorMessage) => {
            const fieldName = key ? `Field '${key}'` : 'General';
            toastrService.warning(`${fieldName}: ${errorMessage}`, 'Validation', {
                timeOut: 15000,
            });
        });
    });

    // Handle global errors (empty key)
    if (errors['']) {
        validationErrorsHandled = true;
        errors[''].forEach((errorMessage) => {
            toastrService.error(`General: ${errorMessage}`, 'Error', {
                timeOut: 30000,
            });
        });
    }

    return validationErrorsHandled;
};
